import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Award, Building, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Certificate } from '../../lib/supabase';
import { ImageUpload } from './ImageUpload';
import { useSimpleProfileData } from '../../hooks/useProfileData';

interface CertificateFormData {
  certificate_name: string;
  issuing_institution: string;
  issue_date: string;
  expiry_date: string;
  certificate_number: string;
  category: string;
  description: string;
  status: 'active' | 'expired' | 'pending';
  image_url: string;
}

const initialFormData: CertificateFormData = {
  certificate_name: '',
  issuing_institution: '',
  issue_date: '',
  expiry_date: '',
  certificate_number: '',
  category: 'academic',
  description: '',
  status: 'active',
  image_url: ''
};

const categoryOptions = [
  { value: 'academic', label: 'Akademik' },
  { value: 'professional', label: 'Profesional' },
  { value: 'skill', label: 'Keahlian' },
  { value: 'language', label: 'Bahasa' },
  { value: 'technology', label: 'Teknologi' },
  { value: 'other', label: 'Lainnya' }
];

const statusOptions = [
  { value: 'active', label: 'Aktif' },
  { value: 'expired', label: 'Kedaluwarsa' },
  { value: 'pending', label: 'Menunggu' }
];

export const CertificateManagement: React.FC = () => {
  const { profileData: profile } = useSimpleProfileData();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CertificateFormData>(initialFormData);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .order('issue_date', { ascending: false });

      if (error) throw error;
      setCertificates(data || []);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError('Gagal memuat data sertifikat');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CertificateFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Ensure we have a valid profile_id
      if (!profile?.id) {
        throw new Error('Profile ID tidak ditemukan. Pastikan profil sudah dibuat.');
      }

      const certificateData = {
        certificate_name: formData.certificate_name,
        issuing_institution: formData.issuing_institution,
        issue_date: formData.issue_date,
        expiry_date: formData.expiry_date || null,
        certificate_number: formData.certificate_number || null,
        category: formData.category,
        description: formData.description || null,
        status: formData.status,
        image_url: formData.image_url || null,
        profile_id: profile.id
      };

      if (editingId) {
        // Update existing certificate
        const { error } = await supabase
          .from('certificates')
          .update(certificateData)
          .eq('id', editingId);

        if (error) throw error;
        setSuccess('Data sertifikat berhasil diperbarui!');
      } else {
        // Create new certificate
        const { error } = await supabase
          .from('certificates')
          .insert([certificateData]);

        if (error) throw error;
        setSuccess('Data sertifikat berhasil ditambahkan!');
      }

      setFormData(initialFormData);
      setShowForm(false);
      setEditingId(null);
      fetchCertificates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving certificate:', err);
      setError('Gagal menyimpan data sertifikat');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (certificate: Certificate) => {
    // Ensure status is valid, default to 'active' if not
    const validStatus = ['active', 'expired', 'pending'].includes(certificate.status) 
      ? certificate.status as 'active' | 'expired' | 'pending'
      : 'active';
    
    setFormData({
      certificate_name: certificate.certificate_name,
      issuing_institution: certificate.issuing_institution,
      issue_date: certificate.issue_date,
      expiry_date: certificate.expiry_date || '',
      certificate_number: certificate.certificate_number || '',
      category: certificate.category,
      description: certificate.description || '',
      status: validStatus,
      image_url: certificate.image_url || ''
    });
    setEditingId(certificate.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus data sertifikat ini?')) {
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('certificates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuccess('Data sertifikat berhasil dihapus!');
      fetchCertificates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting certificate:', err);
      setError('Gagal menghapus data sertifikat');
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setShowForm(false);
    setEditingId(null);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Aktif', color: 'bg-green-100 text-green-800' },
      expired: { label: 'Kedaluwarsa', color: 'bg-red-100 text-red-800' },
      pending: { label: 'Menunggu', color: 'bg-yellow-100 text-yellow-800' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getCategoryBadge = (category: string) => {
    const categoryConfig = {
      academic: { label: 'Akademik', color: 'bg-blue-100 text-blue-800' },
      professional: { label: 'Profesional', color: 'bg-green-100 text-green-800' },
      skill: { label: 'Keahlian', color: 'bg-purple-100 text-purple-800' },
      language: { label: 'Bahasa', color: 'bg-orange-100 text-orange-800' },
      technology: { label: 'Teknologi', color: 'bg-indigo-100 text-indigo-800' },
      other: { label: 'Lainnya', color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = categoryConfig[category as keyof typeof categoryConfig] || categoryConfig.other;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Memuat data sertifikat...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kelola Sertifikat</h1>
            <p className="mt-1 text-sm text-gray-500">
              Tambah, edit, atau hapus informasi sertifikat yang akan ditampilkan di portofolio
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Tambah Sertifikat
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="mb-6 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {editingId ? 'Edit Data Sertifikat' : 'Tambah Data Sertifikat Baru'}
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="certificate_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nama Sertifikat *
                </label>
                <input
                  type="text"
                  id="certificate_name"
                  required
                  value={formData.certificate_name}
                  onChange={(e) => handleInputChange('certificate_name', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nama sertifikat yang diperoleh"
                />
              </div>

              <div>
                <label htmlFor="issuing_institution" className="block text-sm font-medium text-gray-700 mb-2">
                  Institusi Penerbit *
                </label>
                <input
                  type="text"
                  id="issuing_institution"
                  required
                  value={formData.issuing_institution}
                  onChange={(e) => handleInputChange('issuing_institution', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nama institusi yang menerbitkan sertifikat"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="issue_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Terbit *
                </label>
                <input
                  type="date"
                  id="issue_date"
                  required
                  value={formData.issue_date}
                  onChange={(e) => handleInputChange('issue_date', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Kedaluwarsa
                </label>
                <input
                  type="date"
                  id="expiry_date"
                  value={formData.expiry_date}
                  onChange={(e) => handleInputChange('expiry_date', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori *
                </label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  id="status"
                  required
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'expired' | 'pending')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="certificate_number" className="block text-sm font-medium text-gray-700 mb-2">
                Nomor Sertifikat
              </label>
              <input
                type="text"
                id="certificate_number"
                value={formData.certificate_number}
                onChange={(e) => handleInputChange('certificate_number', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nomor atau kode sertifikat (opsional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gambar Sertifikat
              </label>
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => handleInputChange('image_url', url)}
                bucket="portfolio-images"
                folder="certificates"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Deskripsi
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Deskripsi sertifikat, pencapaian, atau catatan khusus..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Batal
              </button>
              <button
                type="submit"
                disabled={saving || !formData.certificate_name || !formData.issuing_institution || !formData.issue_date}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingId ? 'Perbarui' : 'Simpan'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Certificate List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Daftar Sertifikat ({certificates.length})
          </h3>
        </div>

        {certificates.length === 0 ? (
          <div className="p-6 text-center">
            <Award className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada data sertifikat</h3>
            <p className="mt-1 text-sm text-gray-500">
              Mulai dengan menambahkan sertifikat pertama.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Tambah Sertifikat
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sertifikat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institusi & Kategori
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tanggal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {certificates.map((certificate) => (
                    <tr key={certificate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {certificate.certificate_name}
                          </div>
                          {certificate.certificate_number && (
                            <div className="text-xs text-gray-400">
                              No. {certificate.certificate_number}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            <Building className="h-3 w-3 mr-1" />
                            {certificate.issuing_institution}
                          </div>
                          <div className="mt-1">
                            {getCategoryBadge(certificate.category)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <div>
                            {new Date(certificate.issue_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {certificate.expiry_date && (
                              <div className="text-xs text-gray-400">
                                Berlaku hingga: {new Date(certificate.expiry_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(certificate.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(certificate)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(certificate.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};